import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { downloadPdf } from "utils/common";
import { pdfBlob } from "store/priceQuote/actionCreator";
import debounce from "lodash/debounce";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import axios from "services/config";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import { formatPdfPhone } from "utils/common";
import { isNil } from "lodash";
import "./style.scss";

const CoverLetter = ({ territoryAccess }) => {
  const { quoteId } = useParams();
  const { sub } = useSelector((state) => state.loggedInUser);
  const { data: employee, isFetching: fetchingEmployee } = api.useGetEmployeeQuery(sub);
  const { data: quote, isFetching: fetchingQuote } = api.useGetPriceQuoteQuery(quoteId);
  const { data: coverLetter, isFetching: fetchingCoverLetter } = api.useGetCoverLetterQuery(quoteId);
  const [state, setState] = useState();
  const [updateCoverLetter] = api.useUpdateCoverLetterMutation();

  // backend currently does not have access to a user session, so it does not have access to employee phone/email
  // this is a temporary workaround to set a default cover letter body until user sessions are implemented in backend
  const stillLoading = fetchingEmployee || fetchingQuote || fetchingCoverLetter;
  useEffect(() => {
    if (!stillLoading && isNil(coverLetter?.coverLetterBody)) {
      updateCoverLetter({
        quoteId,
        coverLetterBody:
          `Dear ${quote.contactName}:\n\n` +
          `Thank you for considering Sherwin-Williams floorcovering for your project. Included is the Sherwin-Williams price quote.\n\n` +
          `Should you require assistance or have any questions or concerns, please contact me at ${employee.phone ? formatPdfPhone(employee.phone) : ""} or email me at ${employee.email ?? ""}.`,
      });
    }
  }, [stillLoading, isNil(coverLetter?.coverLetterBody)]);

  const debouncedSave = useCallback(
    debounce((state) => {
      toast.promise(updateCoverLetter({ quoteId, ...state }), {
        pending: "Saving changes",
        success: "Changes saved",
        error: "Something went wrong",
      });
    }, 1000),
    [],
  );

  const onChange = (state) => {
    setState(state);
    debouncedSave(state);
  };

  return (
    <div className="pdfOptions cover-page">
      <div className="pdfOptions cover-page--title">Cover Letter Information</div>
      <div className="row">
        <div className="col-12 col-sm-12 col-md-12 col-lg-8 search-form-container__fields__form-group">
          <div className="form-group">
            <label htmlFor="LaborCodeDescription" className="laborCode-Modal__fields__form-group__label">
              <span className="laborCode-Modal__fields__form-group__label--text">Body Text</span>
            </label>
            <textarea
              className="form-control search-form-container__fields__form-group__item search-form-container__fields__form-group__item text-area-pdf"
              id="body"
              value={state?.coverLetterBody ?? coverLetter?.coverLetterBody}
              onChange={(e) => onChange({ ...state, coverLetterBody: e.target.value })}
              disabled={!territoryAccess}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-sm-12 col-md-12 col-lg-8 search-form-container__fields__form-group">
          <div className="form-group">
            <label htmlFor="LaborCodeDescription" className="laborCode-Modal__fields__form-group__label">
              <span className="laborCode-Modal__fields__form-group__label--text">Signature</span>
            </label>
            <textarea
              className="form-control search-form-container__fields__form-group__item search-form-container__fields__form-group__item text-area py-10"
              id="signature"
              value={state?.signature ?? coverLetter?.signature}
              onChange={(e) => onChange({ ...state, signature: e.target.value })}
              disabled={!territoryAccess}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const QuoteNotes = ({ territoryAccess }) => {
  const { quoteId } = useParams();
  const { data: quote } = api.useGetPriceQuoteQuery(quoteId);
  const [quoteNotes, setQuoteNotes] = useState();
  const [updateQuote] = api.useUpdatePriceQuoteMutation();

  const debouncedSave = useCallback(
    debounce((quoteNotes) => {
      toast.promise(updateQuote({ quoteId, quoteNotes }), {
        pending: "Saving changes",
        success: "Quote notes saved",
        error: "Something went wrong",
      });
    }, 1000),
    [],
  );

  return (
    <div className="pdfOptions cover-page">
      <div className="pdfOptions cover-page--title">Add a Note</div>
      <div className="row">
        <div className="col-12 col-sm-12 col-md-12 col-lg-8 search-form-container__fields__form-group">
          <div className="form-group">
            <label htmlFor="LaborCodeDescription" className="laborCode-Modal__fields__form-group__label" />
            <textarea
              className="form-control search-form-container__fields__form-group__item search-form-container__fields__form-group__item text-area-pdf"
              id="quoteNotesText"
              value={quoteNotes ?? quote?.quoteNotes}
              onChange={(e) => {
                setQuoteNotes(e.target.value);
                debouncedSave(e.target.value);
              }}
              disabled={!territoryAccess}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const PdfOptions = ({ territoryAccess }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { quoteId } = useParams();
  const { sub } = useSelector((state) => state.loggedInUser);
  const { data: employee, isFetching: fetchingEmployee } = api.useGetEmployeeQuery(sub);
  const { data: pdfOptions, isFetching: fetchingPdfOptions } = api.useGetPdfOptionsQuery(quoteId);
  const { data: quote } = api.useGetPriceQuoteQuery(quoteId);
  const [state, setState] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [updatePdfOptions] = api.useUpdatePdfOptionsMutation();

  // backend currently does not have access to a user session, so it does not have access to employee details
  // this is a temporary workaround to set default fromData until user sessions are implemented in backend
  const stillLoading = fetchingEmployee || fetchingPdfOptions;
  useEffect(() => {
    if (!stillLoading && isNil(pdfOptions?.fromData)) {
      updatePdfOptions({
        quoteId,
        fromData: `${employee.firstname + " " + employee.lastname}\n${employee.jobTitle ? employee.jobTitle + "\n" : ""}${
          employee.phone ? formatPdfPhone(employee.phone) + "\n" : ""
        }${employee.email ?? ""}`,
      });
    }
  }, [stillLoading, isNil(pdfOptions?.fromData)]);

  const save = (state) => {
    toast.promise(updatePdfOptions({ quoteId, ...state }), {
      pending: "Saving changes",
      success: "PDF Options saved",
      error: "Something went wrong",
    });
    setIsSaving(false);
  };

  const debouncedSave = useCallback(debounce(save, 1000), []);

  const onChange = (state, debounce = false) => {
    setIsSaving(true);
    setState(state);
    debounce ? debouncedSave(state) : save(state);
  };

  const { accountNumber, accountName, customerName } = quote ?? {};
  const toData = accountNumber ? `${accountName} - ${accountNumber}\n${customerName}` : "";
  return (
    <>
      <div className="pdfOptions">
        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-6 search-form-container__fields__form-group">
            <div className="form-group">
              <label htmlFor="LaborCodeDescription" className="laborCode-Modal__fields__form-group__label">
                <span className="laborCode-Modal__fields__form-group__label--text">From</span>
              </label>
              <textarea
                className="form-control search-form-container__fields__form-group__item search-form-container__fields__form-group__item text-area py-10"
                id="fromData"
                value={state?.fromData ?? pdfOptions?.fromData}
                onChange={(e) => onChange({ ...state, fromData: e.target.value }, true)}
                disabled={!territoryAccess}
                maxLength={200}
              />
            </div>
          </div>
          <div className="col-12 col-sm-12 col-md-12 col-lg-6 search-form-container__fields__form-group">
            <div className="form-group">
              <label htmlFor="LaborCodeDescription" className="laborCode-Modal__fields__form-group__label">
                <span className="laborCode-Modal__fields__form-group__label--text">To Addressee</span>
              </label>
              <textarea
                className="form-control search-form-container__fields__form-group__item search-form-container__fields__form-group__item text-area py-10"
                id="toData"
                value={state?.toData ?? pdfOptions?.toData ?? toData}
                onChange={(e) => onChange({ ...state, toData: e.target.value }, true)}
                disabled={!territoryAccess}
                maxLength={200}
              />
            </div>
          </div>
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="isCoverLetter"
                checked={state?.coverLetter ?? pdfOptions?.coverLetter}
                onChange={(e) => onChange({ ...state, coverLetter: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Cover Letter</span>
          {(state?.coverLetter ?? pdfOptions?.coverLetter) && <CoverLetter territoryAccess={territoryAccess} />}
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="material"
                checked={state?.material ?? pdfOptions?.material}
                onChange={(e) => onChange({ ...state, material: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Material</span>
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="labor"
                checked={state?.labor ?? pdfOptions?.labor}
                onChange={(e) => onChange({ ...state, labor: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Labor</span>
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="subtotalBeforeTax"
                checked={state?.subtotalBeforeTax ?? pdfOptions?.subtotalBeforeTax}
                onChange={(e) => onChange({ ...state, subtotalBeforeTax: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Subtotal (Before Tax)</span>
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="tax"
                checked={state?.tax ?? pdfOptions?.tax}
                onChange={(e) => onChange({ ...state, tax: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Tax</span>
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="totalWithTax"
                checked={state?.totalWithTax ?? pdfOptions?.totalWithTax}
                onChange={(e) => onChange({ ...state, totalWithTax: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Total with Tax</span>
        </div>
        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="detailSections"
                checked={state?.detailSection ?? pdfOptions?.detailSection}
                onChange={(e) => onChange({ ...state, detailSection: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Details Section</span>
        </div>

        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="quoteNotes"
                checked={state?.quoteNotes ?? pdfOptions?.quoteNotes}
                onChange={(e) => onChange({ ...state, quoteNotes: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Quote Notes</span>
          {(state?.quoteNotes ?? pdfOptions?.quoteNotes) && <QuoteNotes territoryAccess={territoryAccess} />}
        </div>

        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="floorPlanNotes"
                checked={state?.floorPlanNotes ?? pdfOptions?.floorPlanNotes}
                onChange={(e) => onChange({ ...state, floorPlanNotes: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Floor Plan Notes</span>
        </div>

        <div className="pdfOptions__options">
          <span className="pdfOptions pdf-toggle">
            <label className="switch">
              <input
                type="checkbox"
                id="proposalContract"
                checked={state?.proposalContract ?? pdfOptions?.proposalContract}
                onChange={(e) => onChange({ ...state, proposalContract: e.target.checked })}
                disabled={!territoryAccess}
              />
              <span className="slider round"></span>
            </label>
          </span>
          <span className="pdfOptions__options--text spacing">Proposal & Contract</span>
        </div>
      </div>

      <div className="pdfOptions">
        <div className="pdfOptions__title">
          <span className="pdfOptions__title--text">View or Print Quote</span>
        </div>
        <p className="pdfOptions__desc">
          A price quote document is created. The user can view or print the document either by using the preview
          function or by downloading it.{" "}
        </p>
        <Button
          variant="outline-primary"
          onClick={async () => {
            const res = await dispatch(pdfBlob(quoteId));
            res.status === "0" ? downloadPdf(res.pdfData, "Quote_" + quoteId) : toast.error(res.statusDesc);
          }}
          disabled={isSaving}
        >
          View Quote PDF
        </Button>
        <Button
          variant="outline-primary"
          onClick={async () => {
            dispatch(showSpinner());
            const res = await axios.get(`/fc-price-quote/quote/${quoteId}/materialSelectionConfirmationPdf`);
            const { status, pdfData, statusDesc } = res.data;
            dispatch(hideSpinner());
            status === "0"
              ? downloadPdf(pdfData, `Material_Selection_Confirmation_${quoteId}`)
              : toast.error(statusDesc);
          }}
          disabled={isSaving}
        >
          View material Selection Confirmation
        </Button>
      </div>
      <div className="d-flex footer_display justify-content-between">
        <Button variant="outline-primary" onClick={() => navigate(`/priceQuote/${quoteId}#quote-summary`)}>
          Back
        </Button>
        <Button
          className="m-0"
          variant="primary"
          onClick={() => navigate(`/priceQuote/${quoteId}#deliver-quote`)}
          disabled={isSaving}
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default PdfOptions;
